import React, { useState, useEffect } from "react";
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from "reactstrap";
import { CoreOneRosterConfiguration } from "../../../../../../models/CoreOneRosterConfigurationModels";
import { useTranslation } from 'react-i18next';
import CheckboxListItem from "../../../../../misc/CheckboxListItem";
import Copy from "../../../../../misc/Copy";
import IntInput from "../../../../../misc/IntInput";
import NullableIntInputCheckboxText from "../../../../../misc/NullableIntInputCheckboxText";
import InformationMarkingsInput from "../InformationMarkingsInput";
import TeamsFixComponent from "../TeamsFixComponent";
import { GenerateOneRosterClientCredentials, ClearGenerateOneRosterClientCredentials } from "../../../../../../store/OneRoster/OneRosterConfigurationAction";
import { useDispatch, useSelector } from 'react-redux';
import ModalComponent from "../../../../../misc/ModalComponent";
import { EmptyRequestState, RequestState } from "../../../../../../models/RequestState";
import { RootState } from "../../../../../../store/store";
import StatusCode from "../../../../../../util/StatusCode";
import { getSystemNames } from "../../../../../../store/ModuleAction";
import i18n from 'i18next';


export const validator = (configuration: CoreOneRosterConfiguration) => {
    const validation: string[] = [];

    if (configuration.oneToOneModuleType === 'defaultValue') {
        validation.push('oneToOne');
    }
    if (configuration.archiveGracePeriod < 0) {
        validation.push('archiveGracePeriod')
    }

    if ((configuration.deleteGracePeriod === undefined) ||
        (configuration.archiveGracePeriod === 0 && configuration.deleteGracePeriod !== null && configuration.deleteGracePeriod < 14)
        || (configuration.deleteGracePeriod !== null && configuration.deleteGracePeriod < 0)) {
        validation.push('deleteGracePeriod')
    }

    if (Number.isNaN(configuration.teamsFix.timeZoneId)) {
        validation.push('timeZoneId');
    }

    return validation;
}

export const warningMessages = (originalConfiguration: CoreOneRosterConfiguration, updatedConfiguration: CoreOneRosterConfiguration) => {
    const warnings = [];

    if (originalConfiguration.class && !updatedConfiguration.class) {
        warnings.push(i18n.t('oneRosterConfiguration.warningMessages.class', { ns: 'configuration' }))
    }

    if (originalConfiguration.course && !updatedConfiguration.course) {
        warnings.push(i18n.t('oneRosterConfiguration.warningMessages.course', { ns: 'configuration' }))
    }

    if (originalConfiguration.department && !updatedConfiguration.department) {
        warnings.push(i18n.t('oneRosterConfiguration.warningMessages.department', { ns: 'configuration' }))
    }

    if (originalConfiguration.oneToOneModuleType && !updatedConfiguration.oneToOneModuleType) {
        warnings.push(i18n.t('oneRosterConfiguration.warningMessages.oneToOneModuleType', { ns: 'configuration' }))
    }

    return warnings;
}

export const getAdditionalAppTokenScopes = (configuration: CoreOneRosterConfiguration) => {
    let additionalScopes: string[] = [];
    if (configuration?.teamsFix.enabled ?? false) {
        additionalScopes.push("https://microsoft.sharepoint.com/AllSites.FullControl");
    }
    return additionalScopes;
}

interface OneRosterExportConfigurationProps {
    configuration: CoreOneRosterConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreOneRosterConfiguration>) => void
    onValidChange: (arg: boolean) => void 
    setAdditionalAppTokenScopes: (arg: string[]) => void
}

const OneRosterExportConfiguration = (props: OneRosterExportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);
    const dispatch = useDispatch();

    const [open, setOpen] = useState<boolean>(true);
    const [openModal, setOpenModal] = useState<boolean>(false);
    const [clientCredentials, setClientCredentials] = useState<{ clientId?: string, clientSecret?: string }>({
        clientId: props.configuration.clientId,
        clientSecret: props.configuration.clientSecret
    });
    const [oneToOneCheck, setOneToOneCheck] = useState<boolean>(props.configuration.oneToOneModuleType ? true : false);
    const [defaultOneToOneModuleType] = useState<string | null>(props.configuration.oneToOneModuleType); 

    const systemNames = useSelector<RootState, RequestState<string[]>>(state => state.module.systemNames);
    const updateCredentials = useSelector<RootState, EmptyRequestState>(state => state.oneRosterConfiguration.oneRosterClientCredentials);

    const validation = validator(props.configuration);

    useEffect(() => {
        props.setAdditionalAppTokenScopes(getAdditionalAppTokenScopes(props.configuration))
    }, [props.configuration.teamsFix])

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration]);

    useEffect(() => {
        getSystemNames()(dispatch);
    }, [dispatch]);

    const location = window.location.origin;
    const webUrl = `${location}/ims/oneroster/v1p1`;
    const tokenUrl = `${location}/token`;

    const generateClientCredentials = async () => {
        const res = await GenerateOneRosterClientCredentials(props.configuration.id)(dispatch);
        if (res) {
            setClientCredentials({
                clientId: res.clientId,
                clientSecret: res.clientSecret
            })
            closeModal();
        }
    }

    const closeModal = () => {
        setTimeout(() => {
            ClearGenerateOneRosterClientCredentials()(dispatch);
            setOpenModal(false)
        }, 1500);
    }

    const generateClientCredentialsModal = () => {
        return (
            <ModalComponent
                isOpen={openModal}
                toggleModal={() => closeModal()}
                header={t('oneRosterConfiguration.sdsConfiguration.generateClientCredentialModal.header', { ns: 'configuration' })}
                update={() => generateClientCredentials()}
                confirm={t('oneRosterConfiguration.sdsConfiguration.generateClientCredentialModal.generate', { ns: 'configuration' })}
                cancel={t('action.cancel', { ns: 'common' })}
                saveStatus={updateCredentials.code}
            >
                <p>{t('oneRosterConfiguration.sdsConfiguration.generateClientCredentialModal.generateNewConfirm', { ns: 'configuration' })}</p>
            </ModalComponent>
        )
    }

    const updateOneRosterConfiguration = (prop: string, value: string | boolean) => {
        setOneToOneCheck(Boolean(value));
        let moduleType = null;

        if (value) {
            moduleType = defaultOneToOneModuleType || 'defaultValue';
        }
        props.updatePart({ oneToOneModuleType: moduleType })
    };

    return (
        <Card className="edit_module">
            <CardHeader tag="h2">
                <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                {t('modules.OneRosterConfiguration.title', { ns: 'common' })}
            </CardHeader>
            <Collapse isOpen={open}>
                <CardBody>
                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.typeOfTeam.title', { ns: 'configuration' })}</h5>
                        <CheckboxListItem
                            id="class"
                            name="class"
                            text={t('oneRosterConfiguration.typeOfTeam.class', { ns: 'configuration' })}
                            value={props.configuration.class}
                            onChange={(e) => props.updatePart({ class: e.target.checked })}
                            disabled={props.disabled}
                        />
                        <CheckboxListItem
                            text={t('oneRosterConfiguration.typeOfTeam.activity', { ns: 'configuration' })}
                            value={props.configuration.course}
                            id="course"
                            name="course"
                            onChange={(e) => props.updatePart({ course: e.target.checked })}
                            disabled={props.disabled}
                        />
                        <CheckboxListItem
                            text={t('oneRosterConfiguration.typeOfTeam.department', { ns: 'configuration' })}
                            value={props.configuration.department}
                            id="department"
                            name="department"
                            onChange={(e) => props.updatePart({ department: e.target.checked })}
                            disabled={props.disabled}
                        />
                    </FormGroup>

                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.typeToInclude.title', { ns: 'configuration' })} </h5>
                        <CheckboxListItem
                            text={t('oneRosterConfiguration.typeToInclude.custodian', { ns: 'configuration' })}
                            value={props.configuration.includeCustodians}
                            name="includeCustodians"
                            id="includeCustodians"
                            onChange={(e) => props.updatePart({ includeCustodians: e.target.checked })}
                            disabled={props.disabled}
                        />
                    </FormGroup>

                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.provisionTeams.title', { ns: 'configuration' })}</h5>
                        <CheckboxListItem
                            text={t('oneRosterConfiguration.provisionTeams.forceProvision', { ns: 'configuration' })}
                            value={props.configuration.forceTeamProvision}
                            name="forceTeamProvision"
                            id="forceTeamProvision"
                            onChange={(e) => props.updatePart({ forceTeamProvision: e.target.checked })}
                            disabled={props.disabled}
                        />
                    </FormGroup>

                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.lifeCycleManagement.title', { ns: 'configuration' })}</h5>
                        <IntInput
                            title={t('oneRosterConfiguration.lifeCycleManagement.gracePeriod', { ns: 'configuration' })}
                            name="archiveGracePeriod"
                            value={props.configuration.archiveGracePeriod}
                            disabled={props.disabled}
                            updateValue={(name, value) => props.updatePart({ [name]: value })}
                            invalidFeedback={t('oneRosterConfiguration.lifeCycleManagement.invalidGracePeriod', { ns: 'configuration' })}
                            invalid={validation.indexOf('archiveGracePeriod') !== -1}
                        />

                        <NullableIntInputCheckboxText
                            name="deleteGracePeriod"
                            value={props.configuration.deleteGracePeriod}
                            disabled={props.disabled}
                            title={t('oneRosterConfiguration.lifeCycleManagement.deleteGracePeriod', { ns: 'configuration' })}
                            invalidFeedback={props.configuration.archiveGracePeriod === 0 ?
                                t('oneRosterConfiguration.lifeCycleManagement.invalidDeleteGracePeriod', { ns: 'configuration' })
                                : t('oneRosterConfiguration.lifeCycleManagement.invalidGracePeriod', { ns: 'configuration' })}
                            invalid={validation.indexOf('deleteGracePeriod') !== -1}
                            defaultValue={14}
                            onChange={(name, value) => props.updatePart({ [name]: value })}
                            checkboxTitle={t('oneRosterConfiguration.lifeCycleManagement.noDelete', { ns: 'configuration' })}
                        />
                    </FormGroup>

                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.sdsConfiguration.title', { ns: 'configuration' })}</h5>
                        <p><span className="bold">{t('oneRosterConfiguration.sdsConfiguration.url', { ns: 'configuration' })} </span>{webUrl}<Copy value={webUrl} /></p>
                        <p><span className="bold">{t('oneRosterConfiguration.sdsConfiguration.clientId', { ns: 'configuration' })} </span>{clientCredentials.clientId && clientCredentials.clientId} {clientCredentials.clientId && <Copy value={clientCredentials.clientId} />}</p>
                        <p><span className="bold">{t('oneRosterConfiguration.sdsConfiguration.clientSecret', { ns: 'configuration' })} </span>{clientCredentials.clientSecret && clientCredentials.clientSecret} {clientCredentials.clientSecret && <Copy value={clientCredentials.clientSecret} />}</p>
                        <p><span className="bold">{t('oneRosterConfiguration.sdsConfiguration.token', { ns: 'configuration' })} </span>{tokenUrl}<Copy value={tokenUrl} /></p>

                        <Button
                            className="flowsync-btn"
                            disabled={props.disabled}
                            onClick={() => setOpenModal(true)}>
                            {t('oneRosterConfiguration.sdsConfiguration.generateNewBtn', { ns: 'configuration' })}
                        </Button>
                    </FormGroup>

                    {openModal && generateClientCredentialsModal()}

                    <FormGroup>
                        <h5>{t('oneRosterConfiguration.idStudentRegister.title', { ns: 'configuration' })}</h5>
                        <FormGroup check>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    id="oneToOneCheck"
                                    checked={oneToOneCheck}
                                    disabled={props.disabled}
                                    onChange={(e) => updateOneRosterConfiguration('oneToOneCheck', e.target.checked)}
                                />
                                {t('oneRosterConfiguration.idStudentRegister.forward', { ns: 'configuration' })}
                            </Label>
                        </FormGroup>

                        <FormGroup>
                            <Label for="oneToOneModuleType">{t('oneRosterConfiguration.idStudentRegister.importModuleType', { ns: 'configuration' })}</Label>
                            <Input
                                type="select"
                                name="oneToOneModuleType"
                                id="oneToOneModuleType"
                                onChange={(e) => props.updatePart({ oneToOneModuleType: e.target.value })}
                                disabled={!oneToOneCheck || props.disabled}
                                value={props.configuration.oneToOneModuleType || 'defaultValue'}
                                invalid={validation.indexOf('oneToOne') !== -1} >
                                <option value='defaultValue' disabled>{t('oneRosterConfiguration.idStudentRegister.defaultValue', { ns: 'configuration' })}</option>
                                {systemNames.code === StatusCode.COMPLETE &&
                                    systemNames.data.map((type, i) => <option key={i} value={type}>{type}</option>)}
                            </Input>
                            <FormFeedback>{t('oneRosterConfiguration.idStudentRegister.invalidModuleType', { ns: 'configuration' })}</FormFeedback>
                        </FormGroup>
                    </FormGroup>


                    <InformationMarkingsInput
                        selectedValues={props.configuration.activityInformationMarkingFilters}
                        onChange={(name, value) => props.updatePart({ activityInformationMarkingFilters: value })}
                        name='activityInformationMarkingFilters'
                        disabled={props.disabled}
                    />

                    <TeamsFixComponent
                        teamsFix={props.configuration.teamsFix}
                        onChange={x => props.updatePart({ teamsFix: x })}
                        disabled={props.disabled}
                        teamsFixEnabledSaved={(props.configuration.teamsFix?.enabled) ? false : true}
                        validation={validation}
                    />

                </CardBody>
            </Collapse>
        </Card>
    )
}

export default OneRosterExportConfiguration;
